/*
 * FAMA global-shell behaviors.
 * Adds accessible announcement controls without modifying Impact's vendor carousel code.
 */

const setupAnnouncementBar = (root = document) => {
  root.querySelectorAll('announcement-bar').forEach((bar) => {
    if (bar.dataset.famaAccessibilityReady === 'true') return;

    const section = bar.closest('.shopify-section--announcement-bar');
    const status = section?.querySelector('[data-announcement-status]');

    if (!section || !status) return;

    bar.dataset.famaAccessibilityReady = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    let announceNextChange = false;
    let reducedMotion;
    let syncReducedMotion;

    section.querySelectorAll('[is="prev-button"], [is="next-button"]').forEach((button) => {
      button.addEventListener('click', () => {
        announceNextChange = true;
      }, { signal });
    });

    ['swipeleft', 'swiperight'].forEach((eventName) => {
      bar.addEventListener(eventName, () => {
        announceNextChange = true;
      }, { signal });
    });

    bar.addEventListener('carousel:settle', (event) => {
      if (!announceNextChange) return;

      announceNextChange = false;
      status.textContent = '';
      queueMicrotask(() => {
        status.textContent = event.detail.slide.textContent.trim();
      });
    }, { signal });

    const player = bar.player;
    const pauseButton = section.querySelector('[data-announcement-pause]');

    if (player && pauseButton) {
      reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const pausedFor = new Set();

      const syncPlayer = () => {
        const isPaused = pausedFor.size > 0;

        if (isPaused) {
          player.pause();
        } else {
          player.resume(true);
        }

        const isManuallyPaused = pausedFor.has('manual');
        pauseButton.setAttribute('aria-pressed', String(isManuallyPaused));
        pauseButton.setAttribute('aria-label', isManuallyPaused ? 'Resume announcements' : 'Pause announcements');
      };

      const setPauseReason = (reason, shouldPause) => {
        if (shouldPause) {
          pausedFor.add(reason);
        } else {
          pausedFor.delete(reason);
        }

        syncPlayer();
      };

      pauseButton.addEventListener('click', () => {
        setPauseReason('manual', !pausedFor.has('manual'));
      }, { signal });

      section.addEventListener('mouseenter', () => setPauseReason('hover', true), { signal });
      section.addEventListener('mouseleave', () => setPauseReason('hover', false), { signal });
      section.addEventListener('focusin', () => setPauseReason('focus', true), { signal });
      section.addEventListener('focusout', () => {
        queueMicrotask(() => setPauseReason('focus', section.contains(document.activeElement)));
      }, { signal });

      syncReducedMotion = () => setPauseReason('reduced-motion', reducedMotion.matches);
      reducedMotion.addEventListener('change', syncReducedMotion);
      bar.addEventListener('carousel:settle', syncPlayer, { signal });

      ['player:start', 'player:resume'].forEach((eventName) => {
        player.addEventListener(eventName, () => {
          if (pausedFor.size > 0) queueMicrotask(syncPlayer);
        }, { signal });
      });

      if (reducedMotion.matches) {
        setPauseReason('reduced-motion', true);
      }
    }

    section.addEventListener('shopify:section:unload', () => {
      controller.abort();
      reducedMotion?.removeEventListener('change', syncReducedMotion);
      delete bar.dataset.famaAccessibilityReady;
    }, { once: true, signal });
  });
};

const setupFamaHeader = (root = document) => {
  const headers = [
    ...(root.matches?.('store-header.header') ? [root] : []),
    ...root.querySelectorAll('store-header.header'),
  ];

  headers.forEach((header) => {
    if (header.dataset.famaHeaderReady === 'true') return;

    header.dataset.famaHeaderReady = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    const section = header.closest('.shopify-section--header');
    const desktopPointer = window.matchMedia('(min-width: 1150px) and (hover: hover) and (pointer: fine)');
    const mobilePdpOverlayQuery = window.matchMedia('(max-width: 699px)');
    const disclosures = [...header.querySelectorAll('details[is="mega-menu-disclosure"]')];
    const openModality = new WeakMap();
    let latestPointerType;
    let dismissTimer;
    let fillFrame;

    /* Home and the mobile FAMA PDP share one transparent-to-solid header
     * contract. The PDP opts in independently so desktop product pages keep
     * their existing shell while the mobile header can overlay the gallery. */
    const syncTransparentFill = () => {
      fillFrame = undefined;
      const hasMobilePdpOverlay = (
        mobilePdpOverlayQuery.matches
        && document.querySelector('[data-fama-mobile-header-overlay]') !== null
      );
      const shouldManageFill = header.hasAttribute('allow-transparency') || hasMobilePdpOverlay;
      const fillThreshold = hasMobilePdpOverlay ? 24 : 96;

      header.classList.toggle('is-fama-pdp-mobile-overlay', hasMobilePdpOverlay);
      header.classList.toggle(
        'is-fama-filled',
        shouldManageFill && window.scrollY >= fillThreshold,
      );
    };

    const scheduleTransparentFill = () => {
      if (fillFrame !== undefined) return;
      fillFrame = window.requestAnimationFrame(syncTransparentFill);
    };

    const syncMenuOpenState = () => {
      header.classList.toggle(
        'is-fama-menu-open',
        disclosures.some((disclosure) => disclosure.open),
      );
    };

    syncTransparentFill();
    window.addEventListener('scroll', scheduleTransparentFill, { passive: true, signal });
    mobilePdpOverlayQuery.addEventListener('change', scheduleTransparentFill, { signal });

    const cancelDismiss = () => {
      if (dismissTimer === undefined) return;

      window.clearTimeout(dismissTimer);
      dismissTimer = undefined;
    };

    disclosures.forEach((disclosure) => {
      const summary = disclosure.querySelector(':scope > summary');

      disclosure.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') openModality.set(disclosure, 'pointer');
      }, { capture: true, signal });

      summary?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') openModality.set(disclosure, 'keyboard');
      }, { signal });

      summary?.addEventListener('pointerover', (event) => {
        if (
          desktopPointer.matches
          && event.pointerType === 'mouse'
          && disclosure.getAttribute('trigger') === 'hover'
        ) {
          header.classList.add('is-fama-menu-open');
        }
      }, { signal });

      disclosure.addEventListener('toggle', () => {
        if (!disclosure.open) openModality.delete(disclosure);
        queueMicrotask(syncMenuOpenState);
      }, { signal });
    });

    syncMenuOpenState();

    const scheduleDismiss = (event) => {
      if (!desktopPointer.matches || (event.pointerType && event.pointerType !== 'mouse')) return;
      if (dismissTimer !== undefined) return;

      dismissTimer = window.setTimeout(() => {
        dismissTimer = undefined;

        disclosures.forEach((disclosure) => {
          if (!disclosure.open) return;

          const activeElement = document.activeElement;
          const focusIsInside = (
            activeElement instanceof Element
            && disclosure.contains(activeElement)
          );
          const keyboardOpened = openModality.get(disclosure) === 'keyboard';

          /* Pointer-open menus dismiss on pointer leave. A menu opened with
           * Enter/Space stays available while keyboard focus is
           * anywhere inside it, including on the summary trigger itself. */
          if (!(keyboardOpened && focusIsInside)) disclosure.open = false;
        });
      }, 180);
    };

    const pointIsInside = (event, element) => {
      if (!(element instanceof Element)) return false;

      const rect = element.getBoundingClientRect();

      return (
        event.clientX >= rect.left
        && event.clientX <= rect.right
        && event.clientY >= rect.top
        && event.clientY <= rect.bottom
      );
    };

    const handlePointerMove = (event) => {
      const touchGeneratedMouse = event.sourceCapabilities?.firesTouchEvents === true;

      if (
        !desktopPointer.matches
        || touchGeneratedMouse
        || latestPointerType === 'touch'
        || latestPointerType === 'pen'
      ) return;

      const openDisclosures = disclosures.filter((disclosure) => disclosure.open);

      if (openDisclosures.length === 0) {
        cancelDismiss();
        return;
      }

      const pointerIsInsideNav = (
        pointIsInside(event, header)
        || openDisclosures.some((disclosure) => pointIsInside(event, disclosure.querySelector('.mega-menu')))
      );

      if (pointerIsInsideNav) cancelDismiss();
      else scheduleDismiss(event);
    };

    const recordPointerType = (event) => {
      latestPointerType = event.pointerType;
    };

    document.addEventListener('pointerdown', recordPointerType, { capture: true, signal });
    document.addEventListener('pointermove', recordPointerType, { passive: true, signal });
    document.addEventListener('mousemove', handlePointerMove, { passive: true, signal });
    window.addEventListener('pointerout', (event) => {
      if (event.relatedTarget === null) scheduleDismiss(event);
    }, { signal });

    section?.addEventListener('shopify:section:unload', () => {
      controller.abort();
      cancelDismiss();
      if (fillFrame !== undefined) window.cancelAnimationFrame(fillFrame);

      delete header.dataset.famaHeaderReady;
    }, { once: true, signal });
  });
};

/* Keep Impact's debounce, abort controller, focus and slot transitions while
 * narrowing the request to the product fields used by FAMA's PLP card. */
const setupFamaPredictiveSearch = async () => {
  await window.customElements.whenDefined('predictive-search');

  const PredictiveSearch = window.customElements.get('predictive-search');

  if (!PredictiveSearch || PredictiveSearch.prototype.famaProductSearchReady) return;

  const parseProductLimit = (element) => {
    const configuredLimit = Number.parseInt(element.getAttribute('product-limit'), 10);

    if (!Number.isFinite(configuredLimit)) return 6;

    return Math.min(10, Math.max(1, configuredLimit));
  };

  const replaceResultSlot = async (element, response) => {
    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(`Predictive Search request failed (${response.status}): ${responseText}`);
    }

    const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
    const nextResultSlot = responseDocument.querySelector('[slot="results"]');

    if (!nextResultSlot) {
      throw new Error('Predictive Search response did not include a results slot.');
    }

    element.querySelector('[slot="results"]').replaceWith(document.importNode(nextResultSlot, true));
    setupFamaQuickBuyDismissal(element);
    return element._transitionToSlot('results');
  };

  PredictiveSearch.prototype._doPredictiveSearch = async function famaDoPredictiveSearch() {
    await this._transitionToSlot('loading');

    const queryParams = new URLSearchParams();
    queryParams.set('q', this._queryInput.value);
    queryParams.set('section_id', this.getAttribute('section-id'));
    queryParams.set('resources[type]', 'product');
    queryParams.set('resources[limit]', String(parseProductLimit(this)));
    queryParams.set('resources[limit_scope]', 'each');
    queryParams.set('resources[options][unavailable_products]', 'last');
    queryParams.set('resources[options][fields]', 'title,product_type,variants.title');

    const response = await fetch(
      `${window.Shopify.routes.root}search/suggest?${queryParams}`,
      { signal: this._abortController.signal },
    );

    return replaceResultSlot(this, response);
  };

  PredictiveSearch.prototype._doFallbackSearch = async function famaDoFallbackSearch() {
    await this._transitionToSlot('loading');

    const queryParams = new URLSearchParams();
    queryParams.set('q', this._queryInput.value);
    queryParams.set('type', 'product');
    queryParams.set('options[prefix]', 'last');
    queryParams.set('section_id', this.getAttribute('section-id'));

    const response = await fetch(
      `${window.Shopify.routes.root}search?${queryParams}`,
      { signal: this._abortController.signal },
    );

    return replaceResultSlot(this, response);
  };

  Object.defineProperty(PredictiveSearch.prototype, 'famaProductSearchReady', {
    configurable: false,
    value: true,
  });
};

/* Shopify's Predictive Search matches a partial final term automatically,
 * while the standard Search page requires its documented query wildcard.
 * Add it only at submission time so the customer's visible query stays clean. */
const setupFamaSearchSubmission = (root = document) => {
  root.querySelectorAll('.fama-search-form').forEach((form) => {
    if (form.dataset.famaSearchSubmissionReady === 'true') return;

    form.addEventListener('submit', () => {
      const queryInput = form.elements.q;

      if (!(queryInput instanceof HTMLInputElement)) return;

      const query = queryInput.value.trim();

      if (query && !query.endsWith('*')) {
        queryInput.value = `${query}*`;
      }
    });

    form.dataset.famaSearchSubmissionReady = 'true';
  });
};

/* Mobile utility surfaces share one spatial model: navigation exits left,
 * while search and cart enter from the right. The whole opaque surface moves
 * as one layer; text and controls never fade independently. Impact continues
 * to own focus trapping, scroll locking, Escape, and lifecycle events. */
const runFamaMobileDrawerTransition = (
  drawer,
  { opening, direction, shouldAnimate = true },
) => {
  const overlay = drawer.shadowRoot?.querySelector('[part="overlay"]');
  const content = drawer.shadowRoot?.querySelector('[part="content"]');

  if (!overlay || !content) return Promise.resolve();

  const transitionId = (drawer.famaMobileDrawerTransitionId || 0) + 1;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hiddenTransform = direction === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
  const animatedElements = [drawer, overlay, content, ...content.children];

  drawer.famaMobileDrawerTransitionId = transitionId;
  animatedElements.forEach((element) => {
    element.getAnimations().forEach((animation) => animation.cancel());
  });

  content.style.clipPath = 'none';
  content.style.opacity = '1';
  Array.from(content.children).forEach((child) => {
    child.style.opacity = '1';
  });

  if (opening) {
    drawer._setInitialPosition?.();
    drawer.style.visibility = 'visible';
    drawer.style.opacity = '1';
    overlay.style.opacity = '0';
    content.style.transform = hiddenTransform;

    if (!shouldAnimate || reducedMotion) {
      overlay.style.opacity = '1';
      content.style.transform = 'translateX(0)';
      drawer.classList.add('show-close-cursor');
      return Promise.resolve();
    }

    const overlayAnimation = overlay.animate(
      [{ opacity: 0 }, { opacity: 1 }],
      { duration: 110, easing: 'linear', fill: 'both' },
    );
    const contentAnimation = content.animate(
      [{ transform: hiddenTransform }, { transform: 'translateX(0)' }],
      {
        duration: 220,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      },
    );

    return Promise.all([
      overlayAnimation.finished.catch(() => undefined),
      contentAnimation.finished.catch(() => undefined),
    ]).then(() => {
      if (drawer.famaMobileDrawerTransitionId !== transitionId) return;

      overlay.style.opacity = '1';
      content.style.transform = 'translateX(0)';
      overlayAnimation.cancel();
      contentAnimation.cancel();
      drawer.classList.add('show-close-cursor');
    });
  }

  const overlayOpacity = window.getComputedStyle(overlay).opacity;
  const contentTransform = window.getComputedStyle(content).transform;

  if (reducedMotion) {
    drawer.style.visibility = 'hidden';
    drawer.style.opacity = '0';
    drawer.classList.remove('show-close-cursor');
    return Promise.resolve();
  }

  const overlayAnimation = overlay.animate(
    [{ opacity: overlayOpacity }, { opacity: 0 }],
    { duration: 90, easing: 'linear', fill: 'both' },
  );
  const contentAnimation = content.animate(
    [
      { transform: contentTransform === 'none' ? 'translateX(0)' : contentTransform },
      { transform: hiddenTransform },
    ],
    {
      duration: 175,
      easing: 'cubic-bezier(0.4, 0, 1, 1)',
      fill: 'both',
    },
  );

  return Promise.all([
    overlayAnimation.finished.catch(() => undefined),
    contentAnimation.finished.catch(() => undefined),
  ]).then(() => {
    if (drawer.famaMobileDrawerTransitionId !== transitionId) return;

    drawer.style.visibility = 'hidden';
    drawer.style.opacity = '0';
    overlayAnimation.cancel();
    contentAnimation.cancel();
    overlay.style.opacity = '';
    content.style.transform = '';
    content.style.opacity = '';
    Array.from(content.children).forEach((child) => {
      child.style.opacity = '';
    });
    drawer.classList.remove('show-close-cursor');
  });
};

let famaMobileSurfaceOwner;

const setFamaMobileSurfaceMode = (drawer, mode) => {
  famaMobileSurfaceOwner = drawer;

  document.documentElement.classList.toggle('fama-mobile-utility-active', mode === 'light');
  document.body?.classList.toggle('fama-mobile-utility-active', mode === 'light');
};

const releaseFamaMobileSurfaceMode = (drawer) => {
  if (famaMobileSurfaceOwner !== drawer) return;

  famaMobileSurfaceOwner = undefined;

  document.documentElement.classList.remove('fama-mobile-utility-active');
  document.body?.classList.remove('fama-mobile-utility-active');
};

const setupFamaMobileDrawerMotion = async () => {
  await Promise.all([
    window.customElements.whenDefined('navigation-drawer'),
    window.customElements.whenDefined('search-drawer'),
    window.customElements.whenDefined('cart-drawer'),
  ]);

  const mobileViewport = window.matchMedia('(max-width: 699px)');
  const SearchDrawer = window.customElements.get('search-drawer');

  /* Search remains directionally consistent on desktop, where the native
   * drawer transition and sizing continue to apply. */
  if (SearchDrawer && !SearchDrawer.prototype.famaOpenFromReady) {
    Object.defineProperty(SearchDrawer.prototype, 'openFrom', {
      configurable: true,
      get: () => 'right',
    });
    Object.defineProperty(SearchDrawer.prototype, 'famaOpenFromReady', {
      configurable: false,
      value: true,
    });
  }

  const drawerConfigs = [
    {
      Element: window.customElements.get('navigation-drawer'),
      direction: 'left',
      matches: (drawer) => drawer.id === 'header-sidebar-menu',
      navigation: true,
      surfaceMode: 'dark',
    },
    {
      Element: SearchDrawer,
      direction: 'right',
      matches: (drawer) => drawer.classList.contains('search-drawer--fama'),
      surfaceMode: 'light',
    },
    {
      Element: window.customElements.get('cart-drawer'),
      direction: 'right',
      matches: (drawer) => drawer.classList.contains('fama-cart-drawer'),
      surfaceMode: 'light',
    },
  ];

  drawerConfigs.forEach(({ Element, direction, matches, navigation, surfaceMode }) => {
    if (!Element || Element.prototype.famaMobileDrawerMotionReady) return;

    const originalShowTransition = Element.prototype._showTransition;
    const originalHideTransition = Element.prototype._hideTransition;

    Element.prototype._showTransition = function famaMobileShowTransition(shouldAnimate = true) {
      if (!mobileViewport.matches || !matches(this)) {
        if (navigation) document.body?.classList.remove('fama-mobile-navigation-active');
        releaseFamaMobileSurfaceMode(this);
        return originalShowTransition.call(this, shouldAnimate);
      }

      if (navigation) document.body?.classList.add('fama-mobile-navigation-active');
      setFamaMobileSurfaceMode(this, surfaceMode);

      return runFamaMobileDrawerTransition(this, {
        opening: true,
        direction,
        shouldAnimate,
      });
    };

    Element.prototype._hideTransition = function famaMobileHideTransition() {
      if (!mobileViewport.matches || !matches(this)) {
        if (navigation) document.body?.classList.remove('fama-mobile-navigation-active');
        releaseFamaMobileSurfaceMode(this);
        return originalHideTransition.call(this);
      }

      const finished = runFamaMobileDrawerTransition(this, {
        opening: false,
        direction,
      });
      const transitionId = this.famaMobileDrawerTransitionId;

      finished.finally(() => {
        if (this.famaMobileDrawerTransitionId === transitionId) {
          if (navigation) {
            document.body?.classList.remove('fama-mobile-navigation-active');
          }

          releaseFamaMobileSurfaceMode(this);
        }
      });

      return finished;
    };

    Object.defineProperty(Element.prototype, 'famaMobileDrawerMotionReady', {
      configurable: false,
      value: true,
    });
  });
};

/* Keep the overlay as a visual backdrop. Quick Buy is closed through its
 * explicit X control or the keyboard Escape key, never by clicking outside. */
const setupFamaQuickBuyDismissal = (root = document) => {
  root.querySelectorAll('quick-buy-drawer.fama-quick-buy-modal').forEach((drawer) => {
    if (drawer.dataset.famaDismissalReady === 'true') return;

    const overlay = drawer.shadowRoot?.querySelector('[part="overlay"]');

    if (!overlay) return;

    overlay.addEventListener('click', (event) => {
      event.stopImmediatePropagation();
    }, { capture: true });

    drawer.addEventListener('pointerdown', () => {
      drawer.dataset.famaPointerOpen = 'true';
    }, { capture: true });

    drawer.addEventListener('keydown', () => {
      delete drawer.dataset.famaPointerOpen;
    }, { capture: true });

    drawer.dataset.famaDismissalReady = 'true';
    drawer.classList.remove('show-close-cursor');
  });
};

/* The PLP Quick Buy keeps Impact's lazy loading, focus trap, Escape handling,
 * scroll lock and cart lifecycle. Only its desktop entrance/exit motion is
 * replaced with a stable backdrop fade plus a short 24px rise. */
const waitForFamaQuickBuyMedia = async (drawer) => {
  const gallery = drawer.querySelector('.fama-quick-buy__media-pane media-carousel');
  const mediaItems = gallery?.querySelectorAll('.product-gallery__media');
  const initialIndex = Number.parseInt(gallery?.getAttribute('initial-index') || '0', 10);
  const image = (
    mediaItems?.[initialIndex]?.querySelector('img')
    || drawer.querySelector('.fama-quick-buy__media-pane .product-gallery__media:not([hidden]) img')
  );

  if (!(image instanceof HTMLImageElement)) return;
  if (image.complete && image.naturalWidth > 0) return;

  let timeoutId;
  const timeout = new Promise((resolve) => {
    timeoutId = window.setTimeout(resolve, 700);
  });

  try {
    if (typeof image.decode === 'function') {
      await Promise.race([image.decode(), timeout]);
    } else {
      const settled = new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });

      await Promise.race([settled, timeout]);
    }
  } catch {
    /* A failed decode still falls back to the stable media-pane background. */
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const setupFamaQuickBuyMotion = async () => {
  await window.customElements.whenDefined('quick-buy-drawer');

  const QuickBuyDrawer = window.customElements.get('quick-buy-drawer');

  if (!QuickBuyDrawer || QuickBuyDrawer.prototype.famaQuickBuyMotionReady) return;

  const desktopMotion = window.matchMedia('(min-width: 1000px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const originalShowTransition = QuickBuyDrawer.prototype._showTransition;
  const originalHideTransition = QuickBuyDrawer.prototype._hideTransition;
  let focusPrototype = QuickBuyDrawer.prototype;
  let initialFocusDescriptor;

  while (focusPrototype && !initialFocusDescriptor) {
    initialFocusDescriptor = Object.getOwnPropertyDescriptor(focusPrototype, 'initialFocus');
    focusPrototype = Object.getPrototypeOf(focusPrototype);
  }

  const originalInitialFocus = initialFocusDescriptor?.get;

  if (originalInitialFocus) {
    Object.defineProperty(QuickBuyDrawer.prototype, 'initialFocus', {
      configurable: true,
      get() {
        if (this.classList.contains('fama-quick-buy-modal')) {
          return this.shadowRoot?.querySelector('[part="close-button"]') || originalInitialFocus.call(this);
        }

        return originalInitialFocus.call(this);
      },
    });
  }

  const cancelAnimations = (elements) => {
    elements.filter(Boolean).forEach((element) => {
      element.getAnimations().forEach((animation) => animation.cancel());
    });
  };

  const animationFinished = (animation) => animation.finished.catch(() => undefined);

  QuickBuyDrawer.prototype._showTransition = function famaQuickBuyShowTransition(shouldAnimate = true) {
    const transitionId = (this.famaQuickBuyTransitionId || 0) + 1;
    this.famaQuickBuyTransitionId = transitionId;

    if (!this.classList.contains('fama-quick-buy-modal') || !desktopMotion.matches) {
      delete this.dataset.famaQuickBuyMotion;
      return originalShowTransition.call(this, shouldAnimate);
    }

    const overlay = this.shadowRoot?.querySelector('[part="overlay"]');
    const content = this.shadowRoot?.querySelector('[part="content"]');

    if (!overlay || !content) return originalShowTransition.call(this, shouldAnimate);

    const isReduced = reducedMotion.matches;

    this.dataset.famaQuickBuyMotion = 'active';
    this.classList.remove('show-close-cursor');
    this.style.visibility = 'visible';
    content.style.clipPath = 'none';

    cancelAnimations([overlay, content, ...content.children]);
    overlay.style.opacity = '0';
    content.style.opacity = '0';
    content.style.transform = isReduced ? '' : 'translateY(24px)';

    if (!shouldAnimate) {
      overlay.style.opacity = '1';
      content.style.opacity = '1';
      content.style.transform = '';
      return Promise.resolve();
    }

    const overlayAnimation = overlay.animate(
      [{ opacity: 0 }, { opacity: 1 }],
      {
        duration: isReduced ? 100 : 180,
        easing: 'ease-out',
        fill: 'both',
      },
    );
    let contentAnimation;

    const contentFinished = waitForFamaQuickBuyMedia(this).then(() => {
      if (this.famaQuickBuyTransitionId !== transitionId) return undefined;

      contentAnimation = content.animate(
        isReduced
          ? [{ opacity: 0 }, { opacity: 1 }]
          : [
            { opacity: 0, transform: 'translateY(24px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
        {
          duration: isReduced ? 100 : 220,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          fill: 'both',
        },
      );

      return animationFinished(contentAnimation);
    });

    return Promise.all([
      animationFinished(overlayAnimation),
      contentFinished,
    ]).then(() => {
      if (this.famaQuickBuyTransitionId !== transitionId) return;

      overlay.style.opacity = '1';
      content.style.opacity = '1';
      content.style.transform = '';
      overlayAnimation.cancel();
      contentAnimation?.cancel();
    });
  };

  QuickBuyDrawer.prototype._hideTransition = function famaQuickBuyHideTransition() {
    const transitionId = (this.famaQuickBuyTransitionId || 0) + 1;
    this.famaQuickBuyTransitionId = transitionId;

    if (this.dataset.famaQuickBuyMotion !== 'active') {
      return originalHideTransition.call(this);
    }

    const overlay = this.shadowRoot?.querySelector('[part="overlay"]');
    const content = this.shadowRoot?.querySelector('[part="content"]');

    if (!overlay || !content) return originalHideTransition.call(this);

    const overlayOpacity = window.getComputedStyle(overlay).opacity;
    const contentOpacity = window.getComputedStyle(content).opacity;
    const contentTransform = window.getComputedStyle(content).transform;

    cancelAnimations([overlay, content, ...content.children]);

    const isReduced = reducedMotion.matches;
    const overlayAnimation = overlay.animate(
      [{ opacity: overlayOpacity }, { opacity: 0 }],
      { duration: isReduced ? 80 : 140, easing: 'ease-in', fill: 'both' },
    );
    const contentAnimation = content.animate(
      isReduced
        ? [{ opacity: contentOpacity }, { opacity: 0 }]
        : [
          { opacity: contentOpacity, transform: contentTransform },
          { opacity: 0, transform: 'translateY(24px)' },
        ],
      { duration: isReduced ? 80 : 140, easing: 'ease-in', fill: 'both' },
    );

    return Promise.all([
      animationFinished(overlayAnimation),
      animationFinished(contentAnimation),
    ]).then(() => {
      if (this.famaQuickBuyTransitionId !== transitionId) return;

      this.style.visibility = 'hidden';
      overlay.style.opacity = '0';
      content.style.opacity = '0';
      content.style.transform = 'translateY(24px)';
      overlayAnimation.cancel();
      contentAnimation.cancel();
      overlay.style.opacity = '';
      content.style.opacity = '';
      content.style.transform = '';
      this.classList.remove('show-close-cursor');
      delete this.dataset.famaQuickBuyMotion;
    });
  };

  Object.defineProperty(QuickBuyDrawer.prototype, 'famaQuickBuyMotionReady', {
    configurable: false,
    value: true,
  });
};

/* Every pack card for one product points to one lazy Impact drawer. Updating
 * its handle before Impact opens it preserves exact-variant intent without
 * duplicating forms, component IDs, or cart logic in the collection DOM. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('.fama-plp-card__quick-buy[data-fama-quick-buy-handle]');

  if (!trigger) return;

  const drawerId = trigger.getAttribute('aria-controls');
  const drawer = drawerId ? document.getElementById(drawerId) : null;
  const desiredHandle = trigger.dataset.famaQuickBuyHandle;

  if (!(drawer instanceof HTMLElement) || !desiredHandle) return;

  if (event.detail > 0) {
    drawer.dataset.famaPointerOpen = 'true';
  } else {
    delete drawer.dataset.famaPointerOpen;
  }

  drawer.classList.remove('is-fama-quick-buy-confirmation');

  if (drawer.getAttribute('handle') === desiredHandle) return;

  drawer.setAttribute('handle', desiredHandle);
  drawer._hasLoaded = false;
  drawer.replaceChildren();
}, { capture: true });

/* Impact swaps the form for its native variant-added section. The class lets
 * that confirmation use a compact dialog instead of the product picker shell. */
document.addEventListener('variant:add', (event) => {
  const drawer = event.composedPath().find((node) => (
    node instanceof HTMLElement && node.matches('quick-buy-drawer.fama-quick-buy-modal')
  ));

  if (!drawer) return;

  drawer.classList.add('is-fama-quick-buy-confirmation');

  /* Impact removes the slotted product header when it renders variant-added.
   * Keep an empty slot so the dialog retains its 44px internal close button. */
  const headerSlot = document.createElement('span');
  headerSlot.slot = 'header';
  headerSlot.hidden = true;
  headerSlot.setAttribute('aria-hidden', 'true');
  drawer.prepend(headerSlot);
});

document.addEventListener('DOMContentLoaded', () => {
  setupAnnouncementBar();
  setupFamaHeader();
  setupFamaPredictiveSearch();
  setupFamaSearchSubmission();
  setupFamaMobileDrawerMotion();
  setupFamaQuickBuyDismissal();
  setupFamaQuickBuyMotion();
});

document.addEventListener('shopify:section:load', (event) => {
  setupAnnouncementBar(event.target);
  setupFamaHeader(event.target);
  setupFamaPredictiveSearch();
  setupFamaSearchSubmission(event.target);
  setupFamaMobileDrawerMotion();
  setupFamaQuickBuyDismissal(event.target);
  setupFamaQuickBuyMotion();
});
